import { gql } from '@apollo/client';
import { CSV_MAPPINGS_FIELDS } from '@/graphql/queries/csv_mappings';
import { ORDER_FIELDS } from '@/graphql/queries/orders';
import { ITEM_FIELDS } from '@/graphql/queries/items';
import { LOCATION_FIELDS } from '@/graphql/queries/locations';
import { SERVICES_FIELDS } from '@/graphql/fragments/services';
import { ORDER_REFERENCE_FIELDS } from '@/graphql/fragments/order_references';

export const UPSERT_CSV_MAPPING = gql`
    ${CSV_MAPPINGS_FIELDS}

    mutation UpsertCsvMapping($mapping: csv_mappings_insert_input!) {
        insert_csv_mappings(
            objects: [$mapping]
            on_conflict: { constraint: csv_mappings_pkey, update_columns: [mapping, name] }
        ) {
            returning {
                ...CsvMappingsFields
            }
        }
    }
`;

export const DELETE_CSV_MAPPING = gql`
    mutation DeleteCsvMapping($mapping_id: uuid!) {
        removed: delete_csv_mappings(where: { mapping_id: { _eq: $mapping_id } }) {
            affected_rows
            returning {
                mapping_id
            }
        }
    }
`;

export const UPSERT_ORDERS = gql`
    ${ORDER_FIELDS}
    ${ITEM_FIELDS}
    ${SERVICES_FIELDS}
    ${ORDER_REFERENCE_FIELDS}

    mutation UpsertOrders(
        $orders: [orders_insert_input!]!
        $items: [items_insert_input!]!
        $removals: [uuid!]!
        $item_updates: [items_updates!]! = []
        $tags: [tag_mappings_insert_input!] = []
        $tag_removals: [uuid!] = []
        $documents: [document_mappings_insert_input!] = []
        $order_ids: [uuid!] = []
        $notes: [notes_insert_input!] = []
        $email_subscriptions: [email_order_subscriptions_insert_input!] = []
        $services: [services_insert_input!] = []
        $service_removals: [uuid!] = []
        $references: [order_references_insert_input!] = []
        $reference_removals: [uuid!] = []
    ) {
        # Side inserts need to be before order insert so returning orders are
        # hydrated with the new data
        delete_tag_mappings(where: { mapping_id: { _in: $tag_removals } }) {
            affected_rows
        }
        insert_tag_mappings(objects: $tags, on_conflict: { constraint: tag_order_mappings, update_columns: [] }) {
            affected_rows
        }
        delete_document_mappings(where: { order_id: { _in: $order_ids } }) {
            affected_rows
        }
        insert_document_mappings(
            objects: $documents
            on_conflict: { constraint: document_order_mappings, update_columns: [] }
        ) {
            returning {
                mapping_id
                document_id
                order_id
            }
        }
        delete_email_order_subscriptions(where: { order_id: { _in: $order_ids } }) {
            affected_rows
        }
        insert_email_order_subscriptions(objects: $email_subscriptions) {
            returning {
                subscription_id
            }
        }
        insert_order_references(
            objects: $references
            on_conflict: { constraint: order_references_pkey, update_columns: [key, value, label] }
        ) {
            returning {
                ...OrderReferenceFields
            }
        }
        delete_order_references(where: { reference_id: { _in: $reference_removals } }) {
            affected_rows
            returning {
                reference_id
            }
        }
        insert_orders(
            objects: $orders
            on_conflict: {
                constraint: orders_pkey
                update_columns: [
                    admin_carrier_rate_override
                    admin_middle_mile_carrier_rate_override
                    admin_middle_mile_shipper_rate_override
                    admin_shipper_rate_override
                    assigned_admin_id
                    airline
                    alternative_delivery_dates
                    assembly_required
                    chosen_store_name
                    broker_billed_rate
                    broker_paid_rate
                    bol_file
                    carrier_assignment_source
                    carrier_id
                    carrier_rate
                    carrier_rate_override_id
                    completion_source
                    completion_time
                    geocoding
                    del_elev
                    del_stairs
                    del_window_end
                    del_window_start
                    delayed_by_consignee_ts
                    delivery_date
                    delivery_stair_quantity
                    distance
                    distance_failed
                    dropoff_address
                    dropoff_address_geocode_failed
                    dropoff_address_is_custom
                    dropoff_city
                    dropoff_email
                    dropoff_full_address
                    dropoff_lat
                    dropoff_lng
                    dropoff_location
                    dropoff_location_info
                    dropoff_location_type
                    dropoff_name
                    dropoff_phone
                    dropoff_state
                    dropoff_street
                    dropoff_unit
                    dropoff_zip
                    duration_seconds
                    equipment_overrides
                    estimated_delivery_range_end
                    estimated_delivery_range_start
                    first_available_date
                    haulaway_items
                    has_middle_mile_origin
                    is_middle_mile
                    is_same_day
                    known_availability
                    distance_integration_type
                    last_modified_by
                    last_modified_source
                    manufacturer
                    middle_mile_carrier_id
                    middle_mile_carrier_rate
                    middle_mile_carrier_rate_override_id
                    middle_mile_origin_address
                    middle_mile_origin_city
                    middle_mile_origin_email
                    middle_mile_origin_lat
                    middle_mile_origin_lng
                    middle_mile_origin_location
                    middle_mile_origin_location_info
                    middle_mile_origin_location_type
                    middle_mile_origin_name
                    middle_mile_origin_phone
                    middle_mile_origin_state
                    middle_mile_origin_unit
                    middle_mile_origin_zip
                    middle_mile_shipper_rate
                    middle_mile_shipper_rate_override_id
                    miles
                    pc_miler_version
                    no_contact
                    oms
                    order_revenue
                    order_revenue_adjustments
                    order_revenue_override
                    order_status
                    order_type
                    order_updated_hash
                    pallet_debris_removal_notes
                    pallet_flatbed_truck
                    pallet_forklift_at_dropoff
                    pallet_forklift_at_pickup
                    pallet_unloading_required
                    pallet_warehouse_close_time
                    palletize_returns
                    palletized_dimensions
                    pickup_address
                    pickup_address_geocode_failed
                    pickup_address_is_custom
                    pickup_city
                    pickup_date
                    pickup_email
                    pickup_full_address
                    pickup_lat
                    pickup_lng
                    pickup_location
                    pickup_location_info
                    pickup_location_type
                    pickup_name
                    pickup_phone
                    pickup_stair_quantity
                    pickup_state
                    pickup_street_address
                    pickup_unit
                    pickup_window_end
                    pickup_window_start
                    pickup_zip
                    pin_labels
                    po_duplicate_override
                    po_number
                    preferred_delivery_date
                    price_breakdown
                    recommended_equipment
                    reference_id
                    secondary_dropoff_contact_email
                    secondary_dropoff_contact_name
                    secondary_dropoff_contact_phone
                    secondary_pickup_contact_email
                    secondary_pickup_contact_name
                    secondary_pickup_contact_phone
                    selected_type
                    service_level_id
                    service_level_instructions
                    shipper_id
                    shipper_rate
                    shipper_rate_override_id
                    show_revenue_on
                    source_form
                    special_instructions
                    warehouse_control_entity
                    warehouse_manifest_number
                    waybill
                    pickup_notification
                    warehouse_estimated_ship_date
                    warehouse_estimated_delivery_date
                ]
            }
        ) {
            returning {
                ...OrderFields
            }
        }
        insert_items(
            objects: $items
            on_conflict: {
                constraint: items_pkey
                update_columns: [
                    assembly_type
                    class
                    cubes_per_unit
                    description
                    height
                    is_return
                    item_piece_count
                    item_type
                    item_type_details
                    length
                    line_id
                    nfmc_no
                    quantity
                    rate_class
                    sku
                    total_cubes
                    total_weight
                    weight
                    width
                    service_time
                    disposition
                ]
            }
        ) {
            returning {
                ...ItemFields
            }
        }
        items_updated: update_items_many(updates: $item_updates) {
            affected_rows
            returning {
                ...ItemFields
            }
        }
        delete_items(where: { item_id: { _in: $removals } }) {
            affected_rows
            returning {
                item_id
            }
        }
        insert_services(
            objects: $services
            on_conflict: {
                constraint: services_pkey
                update_columns: [code, type, description, quantity, size, billable, service_time]
            }
        ) {
            returning {
                ...ServicesFields
            }
        }
        delete_services(where: { service_id: { _in: $service_removals } }) {
            affected_rows
            returning {
                service_id
            }
        }
    }
`;

export const UPSERT_ORDER_DYNAMIC = gql`
    ${ORDER_FIELDS}
    ${ITEM_FIELDS}

    mutation UpsertOrdersDynamic(
        $orders: [orders_insert_input!]!
        $items: [items_insert_input!]!
        $removals: [uuid!]!
        $order_update_cols: [orders_update_column!]! = []
        $item_update_cols: [items_update_column!]! = []
    ) {
        insert_orders(objects: $orders, on_conflict: { constraint: orders_pkey, update_columns: $order_update_cols }) {
            returning {
                ...OrderFields
            }
        }
        insert_items(objects: $items, on_conflict: { constraint: items_pkey, update_columns: $item_update_cols }) {
            returning {
                ...ItemFields
            }
        }
        delete_items(where: { item_id: { _in: $removals } }) {
            affected_rows
            returning {
                item_id
            }
        }
    }
`;

export const DELETE_ORDER = gql`
    ${ORDER_FIELDS}
    mutation DeleteOrder($order_id: uuid!) {
        removed: update_orders(
            where: { order_id: { _eq: $order_id } }
            _set: { job_id: null, order_status: "deleted" }
        ) {
            returning {
                ...OrderFields
            }
        }
    }
`;

export const CONFIRM_ORDERS = gql`
    mutation ConfirmOrders($order_ids: [uuid!] = []) {
        confirmed_pending: update_orders(
            where: {
                _and: [
                    { order_id: { _in: $order_ids } }
                    { order_status: { _eq: "pending_confirmation" } }
                    { carrier_id: { _is_null: true } }
                ]
            }
            _set: { order_status: "pending" }
        ) {
            affected_rows
            returning {
                order_id
                oms
                order_status
                carrier_id
            }
        }

        confirmed_claimed: update_orders(
            where: {
                _and: [
                    { order_id: { _in: $order_ids } }
                    { order_status: { _eq: "pending_confirmation" } }
                    { carrier_id: { _is_null: false } }
                ]
            }
            _set: { order_status: "claimed" }
        ) {
            affected_rows
            returning {
                order_id
                shipper_rate_override_id
            }
        }
    }
`;

export const OVERRIDE_DUPLICATES = gql`
    mutation OverrideDuplicates($order_ids: [uuid!] = []) {
        updated: update_orders(
            where: { _and: [{ order_id: { _in: $order_ids } }, { order_status: { _eq: "duplicate" } }] }
            _set: { order_status: "pending_confirmation", po_duplicate_override: "DUPLICATE" }
        ) {
            affected_rows

            returning {
                order_id
                order_number
                order_status
                po_duplicate_override
            }
        }
    }
`;

export const CLEAR_DUPLICATES = gql`
    mutation ClearDuplicates($order_ids: [uuid!] = []) {
        removed: update_orders(
            where: { _and: [{ order_id: { _in: $order_ids } }, { order_status: { _eq: "duplicate" } }] }
            _set: { job_id: null }
        ) {
            affected_rows

            returning {
                order_id
                order_number
                order_status
                po_duplicate_override
            }
        }
    }
`;

export const UPDATE_ITEM_PIECE_COUNTS = gql`
    mutation UpdateItemPieceCounts(
        $item_updates: [items_updates!]! = {}
        $order_id_string: String
        $client_id: uuid!
        $order_id: uuid!
    ) {
        update_items_many(updates: $item_updates) {
            returning {
                item_id
                item_piece_count
            }
        }
        update_orders_by_pk(pk_columns: { order_id: $order_id }, _set: { piece_count_verified: true }) {
            order_id
            piece_count_verified
        }
        update_notifications(
            where: {
                notification_data: { _cast: { String: { _ilike: $order_id_string } } }
                notification_status: { _eq: "UNRESOLVED" }
                notification_type: { _eq: "CUSTOMER_SHIPMENT_FORM_PIECE_COUNT_VERIFICATION" }
                user_id: { _eq: $client_id }
            }
            _set: { notification_status: "RESOLVED" }
        ) {
            returning {
                user_id
                notification_type
                notification_status
                notification_id
                notification_data
            }
        }
    }
`;

export const DELETE_SAVED_CUSTOMER = gql`
    ${LOCATION_FIELDS}
    mutation DeleteSavedCustomer($location_id: uuid!) {
        delete_locations_by_pk(location_id: $location_id) {
            ...LocationFields
        }
    }
`;

export const INSERT_SAVED_CUSTOMER_LOCATION = gql`
    ${LOCATION_FIELDS}
    mutation InsertSavedCustomerLocation($customer: locations_insert_input!) {
        insert_locations_one(object: $customer) {
            ...LocationFields
        }
    }
`;
